"use client";

import { LandingData } from "@/types/types";
import { HeroSection } from "./sections/HeroSection";
import {
  HOW_TO_PLAY_SECTION_ID,
  HowToPlaySection,
} from "./sections/HowToPlaySection";
import { PrizeDetailSection } from "./sections/PrizeDetailSection";
import { CallToActionSection } from "./sections/CallToActionSection";
import styled from "styled-components";
import { Colors } from "@/theme";
import { Footer } from "./Footer";
import { useRouter } from "next/navigation";
import { Routes, TriviaRoutesPublic } from "../trivia/routes";
import { WhoCanPlaySection } from "./sections/WhoCanPlaySection";
import { useRedirect } from "@/hooks/use-redirect";
import { TriviaConfig } from "@/types/model";

/**
 * Types
 */

interface LandingProps {
  sections: LandingData["sections"];
  acquisition?: LandingData["acquisition"];
  triviaSlug: string;
  config?: TriviaConfig;
}

/**
 * Styled Components
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${Colors.white};
`;

/**
 * Landing Component
 */

export const Landing = ({
  sections,
  acquisition,
  triviaSlug,
  config,
}: LandingProps) => {
  const { push } = useRouter();
  const { redirect } = useRedirect();

  const handleRedirectToGame = () => {
    redirect(Routes.LOGIN);
  };

  const handleHowToPlay = () => {
    const element = document.getElementById(HOW_TO_PLAY_SECTION_ID);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleRedirectToTermsAndConditions = () => {
    push(
      `${TriviaRoutesPublic.TERMS_AND_CONDITIONS.replace(
        "{slug}",
        triviaSlug
      )}?returnTo=${window.location.pathname}`
    );
  };

  const howToPlayConfig = config?.howToPlay;
  const howToPlayConfigData = howToPlayConfig
    ? {
        title: howToPlayConfig?.basicInfoSection?.title,
        steps: howToPlayConfig?.basicInfoSection?.items.map((item) => ({
          title: item.title,
          description: item.description,
          icon: item.image ?? "",
        })),
        buttonText: howToPlayConfig?.basicInfoSection?.buttonText,
      }
    : null;

  const howToPlayData = howToPlayConfig
    ? howToPlayConfigData
    : sections.howToPlay;

  const prizeDetailConfigData = howToPlayConfig?.rewardSection
    ? {
        title: howToPlayConfig.rewardSection.title,
        details: howToPlayConfig.rewardSection.items.map((item) => ({
          title: item.title,
          description: item.description,
          icon: item.image ?? "",
        })),
        buttonText: howToPlayConfig.rewardSection.buttonText,
      }
    : null;

  const prizeDetailData = howToPlayConfig?.rewardSection
    ? prizeDetailConfigData
    : sections.prizeDetails;

  return (
    <Container>
      <HeroSection
        data={sections.hero}
        handleHowToPlay={handleHowToPlay}
        handleRedirectToGame={handleRedirectToGame}
        handleRedirectToTermsAndConditions={handleRedirectToTermsAndConditions}
        acquisition={acquisition}
      />
      {sections.whoCanPlay && (
        <WhoCanPlaySection
          title={sections.whoCanPlay.title}
          subtitle={sections.whoCanPlay.subtitle}
        />
      )}
      {howToPlayData && (
        <HowToPlaySection
          data={howToPlayData}
          handleRedirectToGame={handleRedirectToGame}
          acquisition={acquisition}
        />
      )}
      {prizeDetailData && (
        <PrizeDetailSection
          data={prizeDetailData}
          handleRedirectToTermsAndConditions={
            handleRedirectToTermsAndConditions
          }
        />
      )}
      {sections.callToAction && (
        <CallToActionSection
          data={sections.callToAction}
          handleRedirectToGame={handleRedirectToGame}
          acquisition={acquisition}
        />
      )}
      <Footer />
    </Container>
  );
};
